import React, { useMemo, useCallback } from 'react'
import type { AppStructureWithFeatures } from '@wix/thunderbolt-symbols'
import { RendererProps } from '../types'
import type { RenderComp } from './StructureComponent'
import type { ElementData } from '@wix/thunderbolt-becky-types'
import { getInflatedItemId, REF_DELIMITER } from '@wix/thunderbolt-commons'
import { getChildScope, getSlotsScope, emptyScope } from './scopesApi'
import type { ScopeData } from './scopesApi'

interface RenderProps {
	children: Array<React.ReactNode>
	backgroundLayer: Array<React.ReactNode>
	pinnedLayer: Array<React.ReactNode>
}

interface useRenderPropsParams {
	shouldUseRenderProps: boolean
	compProps: any
	propsStore: RendererProps['props']
	displayedItemId: string
	compId: string
	parentScope: ScopeData
	isDs: boolean
	components?: Array<string>
	renderComp: (
		propsStore: RendererProps['props'],
		childId: string,
		scopeData: ScopeData,
		displayedItemId?: string
	) => React.ReactElement
}

type UseContainerPropsParams = {
	compStructure: AppStructureWithFeatures['components'][string]
	containersData?: ElementData['containersData']
	slots?: Record<string, string>
	slotNeedsFakeInflation: boolean
	parentScope: ScopeData
	propsStore: RendererProps['props']
	displayedItemId: string
	renderComp: RenderComp
	isDs: boolean
}

type ContainerType = 'template' | 'slot' | 'simple' | 'placeholder'
type RenderContainer =
	| React.ReactNode
	| ((itemId: string, itemIndex: number) => React.ReactNode)
	| ((slotApi?: Record<string, any>) => React.ReactNode)
type ContainerTypeRender = (propertyName: string) => RenderContainer

type ContainerProps = Record<string, RenderContainer>

export const useRenderProps = ({
	shouldUseRenderProps,
	compProps,
	propsStore,
	displayedItemId,
	compId,
	parentScope,
	isDs,
	components,
	renderComp,
}: useRenderPropsParams) => {
	return useMemo(() => {
		const RenderPropsResult: RenderProps = {
			children: [],
			backgroundLayer: [],
			pinnedLayer: [],
		}

		if (!shouldUseRenderProps) {
			return RenderPropsResult
		}

		const { layers } = compProps
		const pinnedSet = new Set(layers?.pinned ?? [])
		const backgroundSet = new Set(layers?.background ?? [])

		const renderChild = (childId: string): React.ReactNode => {
			const childScope = isDs ? getChildScope(compId, parentScope) : emptyScope
			return renderComp(propsStore, childId, childScope, displayedItemId)
		}

		const getLayerKey = (childId: string): keyof RenderProps => {
			if (backgroundSet.has(childId)) {
				return 'backgroundLayer'
			}
			if (pinnedSet.has(childId)) {
				return 'pinnedLayer'
			}
			return 'children'
		}

		return (components ?? []).reduce((acc, childId) => {
			const layer = getLayerKey(childId)
			acc[layer].push(renderChild(childId))
			return acc
		}, RenderPropsResult)
	}, [
		compProps,
		propsStore,
		displayedItemId,
		compId,
		parentScope,
		isDs,
		components,
		shouldUseRenderProps,
		renderComp,
	])
}

export const useContainerProps = ({
	compStructure,
	containersData,
	slots,
	slotNeedsFakeInflation,
	parentScope,
	propsStore,
	displayedItemId,
	renderComp,
	isDs,
}: UseContainerPropsParams): ContainerProps => {
	const renderSlotComp = useCallback(
		(slotCompId?: string, slotApi?: Record<string, any>) => {
			if (!slotCompId) {
				return null
			}

			const firstScopeItem = parentScope.scope.at(0)
			const slotCompIdForScopeGetter =
				slotNeedsFakeInflation && firstScopeItem ? firstScopeItem + REF_DELIMITER + slotCompId : slotCompId
			const slotsScope = getSlotsScope(parentScope, slotCompIdForScopeGetter)

			return renderComp(propsStore, slotCompId, slotsScope, displayedItemId, slotApi)
		},
		[slotNeedsFakeInflation, parentScope, propsStore, displayedItemId, renderComp]
	)

	const renderTemplateComp = useCallback(
		(itemId: string, itemIndex: number, templateCompId?: string) => {
			if (!templateCompId) {
				return null
			}

			const inflatedItemId = getInflatedItemId(itemId, displayedItemId)
			const childScope = isDs
				? getChildScope(templateCompId, parentScope, {
						parentType: 'Repeater',
						scopeId: inflatedItemId,
						itemIndex,
				  })
				: emptyScope

			return renderComp(propsStore, templateCompId, childScope, inflatedItemId)
		},
		[displayedItemId, isDs, parentScope, propsStore, renderComp]
	)

	return useMemo(() => {
		if (!compStructure.builderType) {
			return {}
		}

		const containers: NonNullable<ElementData['containersData']> = containersData || {}

		const containerRenderers: Record<ContainerType, ContainerTypeRender> = {
			template: (propertyName: string) => (itemId: string, itemIndex: number) =>
				renderTemplateComp(itemId, itemIndex, slots?.[propertyName]),
			slot: (propertyName: string) => (slotApi?: Record<string, any>) =>
				renderSlotComp(slots?.[propertyName], slotApi),
			simple: (propertyName: string) => renderSlotComp(slots?.[propertyName]),
			placeholder: (propertyName: string) => renderSlotComp(slots?.[propertyName]),
		}

		return Object.fromEntries(
			Object.entries(containers).map(([propertyName, containerData]) => {
				const renderer = containerRenderers[containerData.containerType as ContainerType]
				if (!renderer) {
					console.error('Unknown container type:', propertyName, containerData)
					return [propertyName, () => null]
				}

				return [propertyName, renderer(propertyName)]
			})
		)
	}, [compStructure, containersData, slots, renderSlotComp, renderTemplateComp])
}
